import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Alert from '../common/Alert';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const AVAILABLE_PERMISSIONS = [
  { id: 'create_sales', label: 'Create Sales' },
  { id: 'process_payments', label: 'Process Payments' },
  { id: 'print_receipts', label: 'Print Receipts' },
  { id: 'manage_products', label: 'Manage Products' },
  { id: 'manage_inventory', label: 'Manage Inventory' },
  { id: 'manage_customers', label: 'Manage Customers' },
  { id: 'manage_debts', label: 'Manage Debts' },
  { id: 'manage_cashiers', label: 'Manage Staff' },
  { id: 'manage_settings', label: 'Manage Settings' },
  { id: 'view_reports', label: 'View Reports' },
  { id: 'view_basic_reports', label: 'View Basic Reports' },
  { id: 'view_customers', label: 'View Customers' },
];

const RolesManagement = () => {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [currentRole, setCurrentRole] = useState({
    role_name: '',
    permissions: {}
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/roles.php');
      setRoles(res.data.roles || []);
    } catch (err) {
      setError('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    setCurrentRole({
      ...role,
      permissions: role.permissions || {}
    });
    setIsEditing(true);
  };

  const handleCreateNew = () => {
    setCurrentRole({
      role_name: '',
      permissions: {}
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await api.delete(`/roles.php/${id}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete role');
    }
  };

  const handlePermissionChange = (permId, checked) => {
    setCurrentRole(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permId]: checked
      }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (currentRole.id) {
        await api.put(`/roles.php/${currentRole.id}`, currentRole);
        toast.success('Role updated');
      } else {
        await api.post('/roles.php', currentRole);
        toast.success('Role created');
      }
      setIsEditing(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save role');
    }
  };

  if (loading) return <div>Loading roles...</div>;

  return (
    <div className="roles-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>Roles & Permissions</h3>
        {!isEditing && (
          <Button variant="primary" onClick={handleCreateNew}>+ New Role</Button>
        )}
      </div>

      {error && <Alert type="error" message={error} />}

      {isEditing ? (
        <Card>
          <h4>{currentRole.id ? 'Edit Role' : 'Create Role'}</h4>
          <form onSubmit={handleSave}>
            <div style={{ marginBottom: '20px' }}>
              <Input
                label="Role Name"
                value={currentRole.role_name}
                onChange={(e) => setCurrentRole({ ...currentRole, role_name: e.target.value })}
                required
                disabled={currentRole.is_default} // Cannot rename default roles
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h5>Permissions</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                {AVAILABLE_PERMISSIONS.map(perm => (
                  <label key={perm.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!currentRole.permissions?.[perm.id] || !!currentRole.permissions?.all}
                      onChange={(e) => handlePermissionChange(perm.id, e.target.checked)}
                      disabled={currentRole.is_default || !!currentRole.permissions?.all}
                    />
                    {perm.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
              {!currentRole.is_default && (
                <Button type="submit" variant="primary">Save Role</Button>
              )}
            </div>
          </form>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {roles.map(role => (
            <Card key={role.id} style={{ padding: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: '1.1rem' }}>{role.role_name}</strong>
                  {role.is_default && <span style={{ marginLeft: '10px', fontSize: '0.8rem', background: 'var(--slate-200)', padding: '2px 6px', borderRadius: '4px' }}>System Default</span>}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button variant="secondary" onClick={() => handleEdit(role)}>View / Edit</Button>
                  {!role.is_default && (
                    <Button variant="danger" onClick={() => handleDelete(role.id)}>Delete</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
